import React, { useState, useEffect, use } from 'react';

import {
  Pane,
  Heading,
  Button,
  TextInputField,
  Table,
  Select,
  SideSheet,
  IconButton,
    Dialog,Checkbox,

  toaster,
  Spinner, TextInput,TextareaField, Textarea, FilePicker, SelectMenu
} from 'evergreen-ui';

import { collection,setDoc, addDoc, getDocs,arrayUnion, updateDoc, deleteDoc, doc,getDoc, writeBatch } from 'firebase/firestore';

import { firestore, storage } from '../appservices/firebase';
import useStore from '../appservices/store';

 
import { v4 as uuidv4 } from 'uuid'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'


const SUPPORT_TIERS = ["Basic", "Premium"];
const AUTH_METHODS = ["OTP", "PIN"];
const AUTH_LEVELS = [
  { label: 'Level 1: General Inquiries', value: 1 },
  { label: 'Level 2: Warranty Claims', value: 2 },
  { label: 'Level 3: Escalated Issues', value: 3 },
];



/**
 * Your Cloud Functions endpoint where AccountManager is deployed.
 * E.g., 'https://us-east1-milli-8fba8.cloudfunctions.net/aiit/acct/account'
 */
const ACCOUNT_MANAGER_ENDPOINT = 'https://us-east1-milli-8fba8.cloudfunctions.net/aiit/acct/account';

/**
 * Role options for Personnel
 */
// Constants for Roles
const ROLES = ["Owner", "IT Admin", "IT Support", "Basic"];

/**
 * Example 'AddPersonnelForm' that calls your server to manage "personnel".
 * 
 * Props:
 * - onClose: function to close the side sheet
 * - fetchPersonnel: function to refresh personnel list in the parent
 * - personToEdit: existing personnel doc (if editing)
 * - defaultBusinessId: the businessId this Personnel belongs to
 */
 

const AddPersonnelForm = ({
  onClose,
  fetchPersonnel,
  personToEdit
}) => {
  /**
   * If `personToEdit` is provided, we assume we are editing existing personnel;
   * otherwise, we are creating a new one.
   */
  const isEditing = !!personToEdit;

  /**
   * Grab the business profile from the Zustand store
   */
  const businessProfile = useStore(state => state.businessProfile);
  const storeBusinessId = businessProfile?.businessId || '';

  /**
   * Initialize form data.
   * If editing, we use fields from `personToEdit`.
   * If creating, we default to the businessId from the store.
   */
  const [formData, setFormData] = useState(() => ({
    businessId: personToEdit?.businessId || storeBusinessId,
    personnelId: personToEdit?.personnelId || '',
    firstName: personToEdit?.firstName || '',
    lastName: personToEdit?.lastName || '',
    email: personToEdit?.email || '',
    phone: personToEdit?.phone || '',
    role: personToEdit?.role || 'Basic',
    permissions: personToEdit?.permissions || [],
    status: personToEdit?.status || 'Active',
  }));

  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * If `personToEdit` changes (e.g. user reopens form with a different person),
   * we update the form data to reflect it. Also, if the store businessId changes
   * and we are not editing, we can update the form's businessId.
   */
  useEffect(() => {
    if (personToEdit) {
      setFormData(prev => ({
        ...prev,
        businessId: personToEdit.businessId || storeBusinessId,
        personnelId: personToEdit.personnelId || '',
        firstName: personToEdit.firstName || '',
        lastName: personToEdit.lastName || '',
        email: personToEdit.email || '',
        phone: personToEdit.phone || '',
        role: personToEdit.role || 'Basic',
        permissions: personToEdit.permissions || [],
        status: personToEdit.status || 'Active',
      }));
    } else {
      // If no personToEdit, ensure businessId is from the store
      setFormData(prev => ({
        ...prev,
        businessId: storeBusinessId
      }));
    }
  }, [personToEdit, storeBusinessId]);

  /**
   * Handle form field changes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Toggle checkboxes for "permissions"
   */
  const handleCheckboxChange = (permission) => {
    setFormData(prev => {
      const hasPermission = prev.permissions.includes(permission);
      const updatedPermissions = hasPermission
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission];
      return { ...prev, permissions: updatedPermissions };
    });
  };

  /**
   * Submit form -> calls the server endpoint for create or update.
   */
  const handleSubmit = async () => {
    const { firstName, lastName, email, businessId } = formData;

    // Basic validation
    if (!businessId) {
      toaster.warning('A valid businessId is required.');
      return;
    }
    if (!firstName || !lastName || !email) {
      toaster.warning('Please fill out first name, last name, and email.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Determine operation: create or update
      const operation = isEditing ? 'update' : 'create';

      // Build the payload to match your AccountManager style
      const payload = {
        entity: 'personnel',
        operation,
        data: {
          businessId: formData.businessId,
          personnelId: formData.personnelId, // Only relevant if updating
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          permissions: formData.permissions,
          status: formData.status,
        }
      };

      // Call your Cloud Function endpoint
      const response = await fetch(ACCOUNT_MANAGER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.error) {
        toaster.danger(result.error);
      } else {
        toaster.success(result.message || 'Personnel saved successfully.');
        if (fetchPersonnel) {
          fetchPersonnel();
        }
        onClose();
      }
    } catch (error) {
      console.error('Error saving personnel:', error);
      toaster.danger('Failed to save personnel. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Pane padding={16} width="100%">
      <Heading size={600} marginBottom={16}>
        {isEditing ? 'Edit Personnel' : 'Add Personnel'}
      </Heading>

      <TextInputField
        label="First Name"
        value={formData.firstName}
        onChange={(e) => handleInputChange('firstName', e.target.value)}
        placeholder="Enter first name"
        required
      />
      <TextInputField
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => handleInputChange('lastName', e.target.value)}
        placeholder="Enter last name"
        required
      />
      <TextInputField
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        placeholder="Enter email address"
        required
      />
      <TextInputField
        label="Phone Number"
        type="tel"
        value={formData.phone}
        onChange={(e) => handleInputChange('phone', e.target.value)}
        placeholder="Enter phone number"
      />

      <Pane marginBottom={16}>
        <Heading size={400} marginBottom={8}>Role</Heading>
        <SelectMenu
          title="Select Role"
          options={ROLES.map(r => ({ label: r, value: r }))}
          selected={formData.role}
          onSelect={(item) => handleInputChange('role', item.value)}
        >
          <Button>{formData.role || 'Select Role'}</Button>
        </SelectMenu>
      </Pane>

      <Pane marginBottom={16}>
        <Heading size={400} marginBottom={8}>Permissions</Heading>
        <Checkbox
          label="Can manage users"
          checked={formData.permissions.includes('manage_users')}
          onChange={() => handleCheckboxChange('manage_users')}
        />
        <Checkbox
          label="Can edit products"
          checked={formData.permissions.includes('edit_products')}
          onChange={() => handleCheckboxChange('edit_products')}
        />
        <Checkbox
          label="Can handle escalations"
          checked={formData.permissions.includes('handle_escalations')}
          onChange={() => handleCheckboxChange('handle_escalations')}
        />
      </Pane>

      <Pane marginBottom={16}>
        <Heading size={400} marginBottom={8}>Status</Heading>
        <SelectMenu
          title="Select Status"
          options={[
            { label: 'Active', value: 'Active' },
            { label: 'Inactive', value: 'Inactive' },
          ]}
          selected={formData.status}
          onSelect={(item) => handleInputChange('status', item.value)}
        >
          <Button>{formData.status || 'Select Status'}</Button>
        </SelectMenu>
      </Pane>

      <Pane marginTop={16} display="flex" justifyContent="flex-end">
        <Button
          appearance="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {isEditing ? 'Update Personnel' : 'Save Personnel'}
        </Button>
      </Pane>
    </Pane>
  );
};






 


/**
 * AddCustomerForm
 *  - If `customerToEdit` is provided, we assume we are editing that customer
 *    (so the operation is "update").
 *  - Otherwise, we are creating a new customer (operation = "create").
 *  - We also assume there's a known businessId or defaultBusinessId,
 *    so we can link the customer to the correct Business.
 */
const AddCustomerForm = ({
  onClose,
  fetchCustomers,
  customerToEdit,
  defaultBusinessId = ''
}) => {
  const isEditing = !!customerToEdit; // Are we editing or creating?

  const [formData, setFormData] = useState({
    // The businessId this customer belongs to
    businessId: defaultBusinessId,
    customerId: customerToEdit?.customerId || '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    productId: '',
    productName: '',
    serialNumber: '',
    purchaseDate: '',
    warrantyStatus: 'Active',
    supportTier: 'Basic',
    subscriptionStatus: 'Inactive',
    authMethod: 'OTP',
    accessLevel: 1,
    // If needed, set up an interactionHistory field:
    interactionHistory: []
  });

  // Populate fields if we're editing an existing customer
  useEffect(() => {
    if (customerToEdit) {
      setFormData(prev => ({
        ...prev,
        businessId: customerToEdit.businessId || defaultBusinessId,
        customerId: customerToEdit.customerId || '',
        firstName: customerToEdit.firstName || '',
        lastName: customerToEdit.lastName || '',
        email: customerToEdit.email || '',
        phone: customerToEdit.phone || '',
        address: customerToEdit.address || '',
        productId: customerToEdit.product?.id || '',
        productName: customerToEdit.product?.name || '',
        serialNumber: customerToEdit.product?.serialNumber || '',
        purchaseDate: customerToEdit.product?.purchaseDate || '',
        warrantyStatus: customerToEdit.product?.warrantyStatus || 'Active',
        supportTier: customerToEdit.supportTier || 'Basic',
        subscriptionStatus: customerToEdit.subscriptionStatus || 'Inactive',
        authMethod: customerToEdit.authMethod || 'OTP',
        accessLevel: customerToEdit.accessLevel || 1,
        interactionHistory: customerToEdit.interactionHistory || [],
      }));
    }
  }, [customerToEdit, defaultBusinessId]);

  /**
   * Handle input changes in the form
   */
  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  /**
   * Submit the form -> calls your Cloud Function to run AccountManager.manage_customers
   */
  const handleSubmit = async () => {
    const {
      businessId, customerId, firstName, lastName, email,
      productId, productName, serialNumber, purchaseDate
    } = formData;

    // Basic validation
    if (!businessId) {
      toaster.warning('A valid businessId is required.');
      return;
    }
    if (!firstName || !lastName || !email || !productId || !productName || !serialNumber || !purchaseDate) {
      toaster.warning('Please fill out all required fields (name, email, product info, etc.).');
      return;
    }

    // Decide whether we're creating or updating
    const operation = isEditing ? 'update' : 'create';

    // Build the payload for the server
    const payload = {
      entity: 'customer',   // Tells your backend to call manage_customers
      operation,
      data: {
        businessId: formData.businessId,
        customerId: formData.customerId, // needed if updating
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        product: {
          id: formData.productId,
          name: formData.productName,
          serialNumber: formData.serialNumber,
          purchaseDate: formData.purchaseDate,
          warrantyStatus: formData.warrantyStatus,
        },
        supportTier: formData.supportTier,
        subscriptionStatus: formData.subscriptionStatus,
        authMethod: formData.authMethod,
        accessLevel: formData.accessLevel,
        interactionHistory: formData.interactionHistory || []
      }
    };

    try {
      const response = await fetch(ACCOUNT_MANAGER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.error) {
        toaster.danger(result.error);
      } else {
        toaster.success(result.message || 'Customer saved successfully.');
        fetchCustomers(); // Refresh list in parent
        onClose();        // Close the side sheet
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      toaster.danger('Failed to save customer. Please try again.');
    }
  };

  return (
    <Pane padding={16} width="100%">
      <Heading size={600} marginBottom={16}>
        {isEditing ? 'Edit Customer' : 'Add Customer'}
      </Heading>
      <Pane display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={16}>
        {/* Customer Identification */}
        <TextInputField
          label="First Name"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          placeholder="Enter first name"
          required
        />
        <TextInputField
          label="Last Name"
          value={formData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
          placeholder="Enter last name"
          required
        />
        <TextInputField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="Enter email address"
          required
        />
        <TextInputField
          label="Phone Number"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          placeholder="Enter phone number"
        />
        <TextInputField
          label="Address"
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          placeholder="Enter address"
        />

        {/* Product Information */}
        <TextInputField
          label="Product ID"
          value={formData.productId}
          onChange={(e) => handleInputChange('productId', e.target.value)}
          placeholder="Enter product ID"
          required
        />
        <TextInputField
          label="Product Name"
          value={formData.productName}
          onChange={(e) => handleInputChange('productName', e.target.value)}
          placeholder="Enter product name"
          required
        />
        <TextInputField
          label="Serial Number"
          value={formData.serialNumber}
          onChange={(e) => handleInputChange('serialNumber', e.target.value)}
          placeholder="Enter serial number"
          required
        />
        <TextInputField
          label="Purchase Date"
          type="date"
          value={formData.purchaseDate}
          onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
          placeholder="Enter purchase date"
          required
        />

        {/* Support Eligibility */}
        <SelectMenu
          title="Warranty Status"
          options={[
            { label: 'Active', value: 'Active' },
            { label: 'Expired', value: 'Expired' },
          ]}
          selected={formData.warrantyStatus}
          onSelect={(item) => handleInputChange('warrantyStatus', item.value)}
        >
          <Button>{formData.warrantyStatus || 'Select Warranty Status'}</Button>
        </SelectMenu>

        <SelectMenu
          title="Support Tier"
          options={SUPPORT_TIERS.map(tier => ({ label: tier, value: tier }))}
          selected={formData.supportTier}
          onSelect={(item) => handleInputChange('supportTier', item.value)}
        >
          <Button>{formData.supportTier || 'Select Support Tier'}</Button>
        </SelectMenu>

        <SelectMenu
          title="Subscription Status"
          options={[
            { label: 'Active', value: 'Active' },
            { label: 'Inactive', value: 'Inactive' },
          ]}
          selected={formData.subscriptionStatus}
          onSelect={(item) => handleInputChange('subscriptionStatus', item.value)}
        >
          <Button>{formData.subscriptionStatus || 'Select Subscription Status'}</Button>
        </SelectMenu>

        <SelectMenu
          title="Authentication Method"
          options={AUTH_METHODS.map(method => ({ label: method, value: method }))}
          selected={formData.authMethod}
          onSelect={(item) => handleInputChange('authMethod', item.value)}
        >
          <Button>{formData.authMethod || 'Select Auth Method'}</Button>
        </SelectMenu>

        {/* Access Control */}
        <SelectMenu
          title="Authorization Level"
          options={AUTH_LEVELS.map(level => ({ label: level.label, value: level.value }))}
          selected={formData.accessLevel}
          onSelect={(item) => handleInputChange('accessLevel', item.value)}
        >
          <Button>{`Level ${formData.accessLevel || '1'}`}</Button>
        </SelectMenu>
      </Pane>

      <Pane marginTop={16} display="flex" justifyContent="flex-end">
        <Button appearance="primary" onClick={handleSubmit}>
          {isEditing ? 'Update Customer' : 'Save Customer'}
        </Button>
      </Pane>
    </Pane>
  );
};



// const AddProductForm = ({
//   onClose,
//   fetchProducts,
//   productToEdit
// }) => {
//   const isEditing = !!productToEdit;

//   // Retrieve businessProfile from Zustand store
//   const businessProfile = useStore((state) => state.businessProfile);
//   const storeBusinessId = businessProfile?.businessId || '';

//   // Initialize form data
//   const [formData, setFormData] = useState(() => ({
//     businessId: productToEdit?.businessId || storeBusinessId,
//     productId: productToEdit?.productId || '', // Relevant if editing
//     name: productToEdit?.name || '',
//     description: productToEdit?.description || '',
//     category: productToEdit?.category || '',
//     brand: productToEdit?.brand || '',
//     modelNumber: productToEdit?.modelNumber || '',
//     price: productToEdit?.price || '',
//     stockStatus: productToEdit?.stockStatus || '',
//     warrantyInfo: productToEdit?.warrantyInfo || '',
//     supportContact: productToEdit?.supportContact || '',
//     attributes: productToEdit?.attributes || [], // Dynamic attributes
//   }));

//   const [isSubmitting, setIsSubmitting] = useState(false);

//   // Predefined product attributes
//   const predefinedAttributes = [
//     'Brand', 'Model Name', 'Special Feature', 'Color', 'Product Dimensions',
//     'Included Components', 'Filter Type', 'Battery Life', 'Voltage', 'Capacity',
//     'Power Source', 'Compatible Devices', 'Form Factor',
//     'Global Trade Identification Number (GTIN)', 'UPC', 'Item Weight',
//     'Manufacturer', 'Country of Origin', 'Item Model Number', 'Batteries Required',
//     'Other',
//   ];

//   // Attributes that should use TextArea for input
//   const textAreaAttributes = [
//     'Special Feature', 'Included Components', 'Product Dimensions', 'Description',
//   ];

//   // Handle input changes
//   const handleInputChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value }));
//   };

//   // Handle dynamic attributes
//   const addAttribute = () => {
//     setFormData((prev) => ({
//       ...prev,
//       attributes: [...prev.attributes, { name: '', value: '' }],
//     }));
//   };

//   const updateAttribute = (index, field, value) => {
//     setFormData((prev) => {
//       const attributes = [...prev.attributes];
//       attributes[index][field] = value;
//       return { ...prev, attributes };
//     });
//   };

//   const removeAttribute = (index) => {
//     setFormData((prev) => {
//       const attributes = [...prev.attributes];
//       attributes.splice(index, 1);
//       return { ...prev, attributes };
//     });
//   };

//   // Handle form submission
// // Handle form submission
// const handleSubmit = async () => {
//   const requiredFields = ['name', 'category', 'brand', 'businessId'];
//   const missing = requiredFields.filter((f) => !formData[f]);

//   if (missing.length > 0) {
//     toaster.warning(`Please fill out all required fields: ${missing.join(', ')}`);
//     return;
//   }

//   const operation = isEditing ? 'update' : 'create';
//   setIsSubmitting(true);

//   console.log('Submitting form:', formData);

//   try {
//     const batch = writeBatch(firestore);

//     // Use UPC as product ID if available, otherwise generate a new Firestore ID
//     let productDocId = formData.productId || formData.attributes.find((attr) => attr.name === 'UPC')?.value;

//     if (!productDocId) {
//       const productRef = doc(collection(firestore, 'Products'));
//       productDocId = productRef.id;
//     }

//     if (isEditing) {
//       // Ensure productId is present for editing
//       if (!productDocId) {
//         toaster.danger('Missing productId for editing mode.');
//         setIsSubmitting(false);
//         return;
//       }

//       const productDocRef = doc(firestore, 'Products', productDocId);
//       batch.update(productDocRef, {
//         ...formData,
//         updatedAt: new Date().toISOString(),
//       });
//     } else {
//       // Create new product document
//       const productDocRef = doc(firestore, 'Products', productDocId);
//       batch.set(productDocRef, {
//         ...formData,
//         productId: productDocId, // Save product ID
//         createdAt: new Date().toISOString(),
//         updatedAt: new Date().toISOString(),
//       });
//     }

//     // Add product to the business's product dictionary
//     const businessProductDictRef = doc(
//       firestore,
//       `Businesses/${formData.businessId}/productdict`,
//       productDocId
//     );

//     batch.set(businessProductDictRef, {
//       name: formData.name,
//       description: formData.description || '', // Optional field
//       category: formData.category,
//       brand: formData.brand,
//       price: formData.price || 0, // Default price if missing
//       stockStatus: formData.stockStatus || 'in stock', // Default stock status
//       updatedAt: new Date().toISOString(),
//     });

//     // Commit all batch operations
//     await batch.commit();
//     toaster.success(isEditing ? 'Product updated successfully!' : 'Product added successfully!');

//     if (fetchProducts) fetchProducts(); // Refresh product list if applicable
//     console.log('Product saved successfully:', formData);

//     onClose(); // Close form/modal
//   } catch (error) {
//     console.error('Error:', error);
//     toaster.danger('Failed to save product. Please try again.');
//   } finally {
//     setIsSubmitting(false);
//   }
// };


//   return (
//     <Pane padding={16} width="100%">
//       <Heading size={600} marginBottom={16}>
//         {isEditing ? 'Edit Product' : 'Add New Product'}
//       </Heading>

//       <Pane display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={16}>
//         <TextInputField
//           label="Product Name"
//           value={formData.name}
//           onChange={(e) => handleInputChange('name', e.target.value)}
//           required
//         />
//         <TextInputField
//           label="Category"
//           value={formData.category}
//           onChange={(e) => handleInputChange('category', e.target.value)}
//           required
//         />
//         <TextInputField
//           label="Brand/Manufacturer"
//           value={formData.brand}
//           onChange={(e) => handleInputChange('brand', e.target.value)}
//           required
//         />
//         <TextInputField
//           label="Model Number"
//           value={formData.modelNumber}
//           onChange={(e) => handleInputChange('modelNumber', e.target.value)}
//         />
//         <TextInputField
//           label="Price (USD)"
//           value={formData.price}
//           onChange={(e) => handleInputChange('price', e.target.value)}
//           type="number"
//         />
//         <SelectMenu
//           title="Stock Status"
//           options={[
//             { label: 'In Stock', value: 'In Stock' },
//             { label: 'Out of Stock', value: 'Out of Stock' },
//             { label: 'Pre-Order', value: 'Pre-Order' },
//           ]}
//           selected={formData.stockStatus}
//           onSelect={(item) => handleInputChange('stockStatus', item.value)}
//         >
//           <Button>{formData.stockStatus || 'Select Stock Status'}</Button>
//         </SelectMenu>
//         <TextInputField
//           label="Warranty Information"
//           value={formData.warrantyInfo}
//           onChange={(e) => handleInputChange('warrantyInfo', e.target.value)}
//         />
//         <TextInputField
//           label="Support Contact Information"
//           value={formData.supportContact}
//           onChange={(e) => handleInputChange('supportContact', e.target.value)}
//         />
//       </Pane>

//       <Pane marginTop={16}>
//         <Heading size={500} marginBottom={8}>Attributes</Heading>
//         {formData.attributes.map((attr, index) => (
//           <Pane key={index} display="flex" alignItems="center" gap={8} marginBottom={8}>
//             <SelectMenu
//               title="Attribute Name"
//               options={predefinedAttributes.map(attr => ({ label: attr, value: attr }))}
//               selected={attr.name}
//               onSelect={(item) => updateAttribute(index, 'name', item.value)}
//             >
//               <Button>{attr.name || 'Select Attribute'}</Button>
//             </SelectMenu>
//             {attr.name === 'Other' && (
//               <TextInput
//                 value={attr.name}
//                 onChange={(e) => updateAttribute(index, 'name', e.target.value)}
//                 placeholder="Custom attribute name"
//               />
//             )}
//             {textAreaAttributes.includes(attr.name) ? (
//               <Textarea
//                 value={attr.value}
//                 onChange={(e) => updateAttribute(index, 'value', e.target.value)}
//                 placeholder="Enter attribute value"
//               />
//             ) : (
//               <TextInput
//                 value={attr.value}
//                 onChange={(e) => updateAttribute(index, 'value', e.target.value)}
//                 placeholder="Enter attribute value"
//               />
//             )}
//             <Button iconBefore="trash" intent="danger" onClick={() => removeAttribute(index)}>
//               Remove
//             </Button>
//           </Pane>
//         ))}
//         <Button iconBefore="plus" onClick={addAttribute}>Add Attribute</Button>
//       </Pane>

//       <Pane marginTop={16} display="flex" justifyContent="flex-end">
//         <Button
//           appearance="primary"
//           isLoading={isSubmitting}
//           disabled={isSubmitting}
//           onClick={handleSubmit}
//         >
//           {isEditing ? 'Update Product' : 'Save Product'}
//         </Button>
//       </Pane>
//     </Pane>
//   );
// };


const predefinedNames = ['Manual', 'Warranty', 'Specification', 'Brochure']

const DocumentUploadForm = ({ productId, onFinish }) => {
  const [docName, setDocName] = useState(predefinedNames[0])
  const [customName, setCustomName] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  const isOther = docName === 'Other'

  const handleUpload = async () => {
    if (!selectedFile) {
      toaster.danger('Please select a file')
      return
    }

    const finalName = isOther ? customName : docName
    if (!finalName.trim()) {
      toaster.danger('Please specify a name for the document')
      return
    }

    setUploading(true)

    try {
      // 1. Upload file to Firebase Storage
      const fileRef = ref(storage, `documents/${productId}/${uuidv4()}-${selectedFile.name}`)
      await uploadBytes(fileRef, selectedFile)
      const downloadURL = await getDownloadURL(fileRef)

      // 2. Update Firestore => append document info to "documents" array
      const productRef = doc(firestore, 'Products', productId)
      const newDoc = {
        id: uuidv4(),
        name: finalName,
        url: downloadURL,
        createdAt: new Date().toISOString()
      }

      await updateDoc(productRef, {
        documents: arrayUnion(newDoc)
      })

      toaster.success('Document uploaded successfully!')
      onFinish && onFinish() // Notify parent
    } catch (err) {
      console.error(err)
      toaster.danger('Failed to upload document.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Pane>
      <Select
        width="100%"
        marginBottom={16}
        value={docName}
        onChange={(e) => setDocName(e.target.value)}
      >
        {predefinedNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
        <option value="Other">Other</option>
      </Select>

      {isOther && (
        <TextInputField
          label="Custom Document Name"
          placeholder="e.g. Special Edition Manual"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          marginBottom={16}
        />
      )}

      <FilePicker
        onChange={(files) => setSelectedFile(files[0])}
        placeholder="Select a file to upload"
        marginBottom={16}
      />

      <Button
        appearance="primary"
        intent="success"
        isLoading={uploading}
        onClick={handleUpload}
      >
        Upload Document
      </Button>
    </Pane>
  )
}

 
// Your AddProductForm component
const AddProductForm = ({ onClose, fetchProducts, productToEdit }) => {
  const isEditing = !!productToEdit

  // Retrieve businessProfile from Zustand store
  const businessProfile = useStore((state) => state.businessProfile)
  const storeBusinessId = businessProfile?.businessId || ''
  const datastoreId = businessProfile?.datastoreId

  // Initialize form data
  const [formData, setFormData] = useState(() => ({
    businessId: productToEdit?.businessId || storeBusinessId,
    productId: productToEdit?.productId || '', // relevant if editing
    name: productToEdit?.name || '',
    description: productToEdit?.description || '',
    category: productToEdit?.category || '',
    brand: productToEdit?.brand || '',
    modelNumber: productToEdit?.modelNumber || '',
    price: productToEdit?.price || '',
    stockStatus: productToEdit?.stockStatus || '',
    warrantyInfo: productToEdit?.warrantyInfo || '',
    supportContact: productToEdit?.supportContact || '',
    attributes: productToEdit?.attributes || [], // dynamic attributes
    documents: productToEdit?.documents || [] // array of { id, name, url }
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Toggles whether to show the DocumentUploadForm
  const [showUploadForm, setShowUploadForm] = useState(false)

  // Predefined product attributes
  const predefinedAttributes = [
    'Brand',
    'Model Name',
    'Special Feature',
    'Color',
    'Product Dimensions',
    'Included Components',
    'Filter Type',
    'Battery Life',
    'Voltage',
    'Capacity',
    'Power Source',
    'Compatible Devices',
    'Form Factor',
    'Global Trade Identification Number (GTIN)',
    'UPC',
    'Item Weight',
    'Manufacturer',
    'Country of Origin',
    'Item Model Number',
    'Batteries Required',
    'Other'
  ]

  // Attributes that should use TextArea
  const textAreaAttributes = [
    'Special Feature',
    'Included Components',
    'Product Dimensions',
    'Description'
  ]

  // ========= HANDLERS ==========
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // -- Dynamic Attributes
  const addAttribute = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { name: '', value: '' }]
    }))
  }

  const updateAttribute = (index, field, value) => {
    setFormData((prev) => {
      const attributes = [...prev.attributes]
      attributes[index][field] = value
      return { ...prev, attributes }
    })
  }

  const removeAttribute = (index) => {
    setFormData((prev) => {
      const attributes = [...prev.attributes]
      attributes.splice(index, 1)
      return { ...prev, attributes }
    })
  }

  // ========== FORM SUBMISSION ==========
  const handleSubmit = async () => {
    const requiredFields = ['name', 'category', 'brand', 'businessId']
    const missing = requiredFields.filter((f) => !formData[f])

    if (missing.length > 0) {
      toaster.warning(`Please fill out all required fields: ${missing.join(', ')}`)
      return
    }

    setIsSubmitting(true)

    try {
      const batch = writeBatch(firestore)

      // Use productId or a value from attributes (like UPC)
      let productDocId =
        formData.productId ||
        formData.attributes.find((attr) => attr.name === 'UPC')?.value

      if (!productDocId) {
        // If no ID found, create a new doc reference
        const productRef = doc(collection(firestore, 'Products'))
        productDocId = productRef.id
      }

      // Product doc reference
      const productDocRef = doc(firestore, 'Products', productDocId)

      // Either update or set
      if (isEditing) {
        batch.update(productDocRef, {
          ...formData,
          updatedAt: new Date().toISOString()
        })
      } else {
        batch.set(productDocRef, {
          ...formData,
          productId: productDocId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }

      // Add to business's product dictionary
      const businessProductDictRef = doc(
        firestore,
        `Businesses/${formData.businessId}/productdict`,
        productDocId
      )

      batch.set(businessProductDictRef, {
        name: formData.name,
        description: formData.description || '',
        category: formData.category,
        brand: formData.brand,
        price: formData.price || 0,
        stockStatus: formData.stockStatus || 'In Stock',
        updatedAt: new Date().toISOString()
      })

      // Update the Datastore
      if (!datastoreId) {
        throw new Error('Datastore ID not found. Ensure it is set in the Zustand store.')
      }

      const datastoreRef = doc(firestore, `Datastore/${datastoreId}`)
      const datastoreDoc = await getDoc(datastoreRef)

      if (datastoreDoc.exists()) {
        const currentProductDict = datastoreDoc.data()?.productDict || {}

        const updatedProductDict = {
          ...currentProductDict,
          [productDocId]: {
            name: formData.name,
            model: formData.modelNumber || '',
            brand: formData.brand
          }
        }

        batch.update(datastoreRef, {
          productDict: updatedProductDict,
          updatedAt: new Date().toISOString()
        })
      } else {
        throw new Error(`Datastore document with ID ${datastoreId} does not exist.`)
      }

      // Commit batch
      await batch.commit()

      toaster.success(
        isEditing ? 'Product updated successfully!' : 'Product added successfully!'
      )

      if (fetchProducts) fetchProducts() // refresh product list if needed

      // Close the form
      onClose()
    } catch (error) {
      console.error('Error:', error)
      toaster.danger('Failed to save product. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // For logging/troubleshooting
  useEffect(() => {
    console.log('Datastore ID:', datastoreId, 'Business ID:', storeBusinessId)
  }, [datastoreId, storeBusinessId])

  // ========== RENDER ==========
  return (
    <Pane padding={16} width="100%">
      <Heading size={600} marginBottom={16}>
        {isEditing ? 'Edit Product' : 'Add New Product'}
      </Heading>

      {/* BASIC FIELDS */}
      <Pane display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={16}>
        <TextInputField
          label="Product Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          required
        />
        <TextInputField
          label="Category"
          value={formData.category}
          onChange={(e) => handleInputChange('category', e.target.value)}
          required
        />
        <TextInputField
          label="Brand/Manufacturer"
          value={formData.brand}
          onChange={(e) => handleInputChange('brand', e.target.value)}
          required
        />
        <TextInputField
          label="Model Number"
          value={formData.modelNumber}
          onChange={(e) => handleInputChange('modelNumber', e.target.value)}
        />
        <TextInputField
          label="Price (USD)"
          value={formData.price}
          onChange={(e) => handleInputChange('price', e.target.value)}
          type="number"
        />
        <SelectMenu
          title="Stock Status"
          options={[
            { label: 'In Stock', value: 'In Stock' },
            { label: 'Out of Stock', value: 'Out of Stock' },
            { label: 'Pre-Order', value: 'Pre-Order' }
          ]}
          selected={formData.stockStatus}
          onSelect={(item) => handleInputChange('stockStatus', item.value)}
        >
          <Button>{formData.stockStatus || 'Select Stock Status'}</Button>
        </SelectMenu>
        <TextInputField
          label="Warranty Information"
          value={formData.warrantyInfo}
          onChange={(e) => handleInputChange('warrantyInfo', e.target.value)}
        />
        <TextInputField
          label="Support Contact Information"
          value={formData.supportContact}
          onChange={(e) => handleInputChange('supportContact', e.target.value)}
        />
      </Pane>

      {/* ATTRIBUTES SECTION */}
      <Pane marginTop={16}>
        <Heading size={500} marginBottom={8}>
          Attributes
        </Heading>
        {formData.attributes.map((attr, index) => (
          <Pane key={index} display="flex" alignItems="center" gap={8} marginBottom={8}>
            <SelectMenu
              title="Attribute Name"
              options={predefinedAttributes.map((a) => ({ label: a, value: a }))}
              selected={attr.name}
              onSelect={(item) => updateAttribute(index, 'name', item.value)}
            >
              <Button>{attr.name || 'Select Attribute'}</Button>
            </SelectMenu>
            {attr.name === 'Other' && (
              <TextInput
                width={160}
                value={attr.name}
                onChange={(e) => updateAttribute(index, 'name', e.target.value)}
                placeholder="Custom attribute name"
              />
            )}
            {textAreaAttributes.includes(attr.name) ? (
              <Textarea
                width={240}
                value={attr.value}
                onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                placeholder="Enter attribute value"
              />
            ) : (
              <TextInput
                width={240}
                value={attr.value}
                onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                placeholder="Enter attribute value"
              />
            )}
            <Button iconBefore="trash" intent="danger" onClick={() => removeAttribute(index)}>
              Remove
            </Button>
          </Pane>
        ))}
        <Button iconBefore="plus" onClick={addAttribute}>
          Add Attribute
        </Button>
      </Pane>

      {/* DOCUMENTS SECTION (visible in EDIT mode only) */}
      {isEditing && (
        <Pane marginTop={24}>
          <Heading size={500} marginBottom={8}>
            Documents
          </Heading>

          {/* List any existing documents */}
          {formData.documents && formData.documents.length > 0 ? (
            formData.documents.map((docItem) => (
              <Pane key={docItem.id} display="flex" alignItems="center" marginBottom={8}>
                <Button
                  appearance="minimal"
                  intent="success"
                  onClick={() => window.open(docItem.url, '_blank')}
                  marginRight={8}
                >
                  {docItem.name}
                </Button>
                {/* Optionally add a delete button here */}
                {/* <Button iconBefore="trash" intent="danger" onClick={() => handleDelete(docItem)}>Delete</Button> */}
              </Pane>
            ))
          ) : (
            <Pane>
              <Heading size={300} color="muted">
                No documents uploaded yet.
              </Heading>
            </Pane>
          )}

          <Pane marginTop={16}>
            <Button iconBefore="upload" onClick={() => setShowUploadForm((prev) => !prev)}>
              {showUploadForm ? 'Cancel' : 'Upload Document'}
            </Button>
          </Pane>

          {/* Conditionally render the upload form */}
          {showUploadForm && (
            <Pane marginTop={16}>
              <DocumentUploadForm
                productId={formData.productId || productToEdit?.productId}
                // Once upload finishes, you might re-fetch the product or update local doc list
                onFinish={async () => {
                  setShowUploadForm(false)
                  // Re-fetch the product to get the updated documents array
                  if (fetchProducts) {
                    await fetchProducts()
                  }
                  // Also you can do a local update if you want to avoid refetch:
                  // e.g. setFormData((prev) => ({ ...prev, documents: [...prev.documents, newDoc] }))
                }}
              />
            </Pane>
          )}
        </Pane>
      )}

      {!isEditing && (
        <Pane marginTop={24}>
          <Heading size={400} color="muted">
            You can upload documents after saving this product.
          </Heading>
        </Pane>
      )}

      {/* SUBMIT BUTTON */}
      <Pane marginTop={32} display="flex" justifyContent="flex-end">
        <Button
          appearance="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {isEditing ? 'Update Product' : 'Save Product'}
        </Button>
      </Pane>
    </Pane>
  )
}






export default AddCustomerForm;
export { AddPersonnelForm, AddProductForm };